import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { Sparkles, Copy, RefreshCw, AlertTriangle, Check, Globe, Tag, Layers, Heart, Columns, ArrowRight, Download, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { generateOne, generateAllFields } from '../lib/prompt';
import { MARKETS, MARKET_LANGUAGE } from '../lib/constants';
import { MarketFlag } from '../lib/MarketFlag';
import { useData } from '../contexts/DataContext';
import { VocabCollectionSelector } from '../components/VocabCollectionSelector';

const MARKET_DISPLAY_TO_CODE = {
  'Sverige': 'SE', 'Norge': 'NO', 'Finland': 'FI',
  'Tyskland': 'DE', 'Danmark': 'DK', 'Nederländerna': 'NL',
};
const MARKET_CODE_TO_DISPLAY = {
  'SE': 'Sverige', 'NO': 'Norge', 'FI': 'Finland',
  'DE': 'Tyskland', 'DK': 'Danmark', 'NL': 'Nederländerna',
};

function countWords(text) {
  return (text || '').trim().split(/\s+/).filter(w => w.length > 0).length;
}
function stripHtml(html) {
  if (!html) return '';
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
}
function countWordsHtml(html) { return countWords(stripHtml(html)); }
const isWordCountValid = wc => wc >= 150 && wc <= 220;

function GenSwitch({ checked, onChange, label, testId }) {
  return (
    <label className="gen-switch-row" data-testid={testId}>
      <span className="gen-switch-label">{label}</span>
      <button type="button" className={`toggle-switch${checked ? ' toggle-switch--on' : ''}`}
        onClick={onChange} aria-checked={checked} role="switch">
        <span className="toggle-knob" />
      </button>
    </label>
  );
}

export default function GeneratePage({ prefill, onPrefillUsed }) {
  /* ── Form state ──────────────────────────────────────────────────────────── */
  const [productName, setProductName] = useState('');
  const [category, setCategory]       = useState('');
  const [features, setFeatures]       = useState('');
  const [selectedMarkets, setSelectedMarkets] = useState([]); // display names

  /* ── Generation switches ─────────────────────────────────────────────────── */
  const [genSwitches, setGenSwitches] = useState({ desc: true, metaTitle: true, metaDesc: true });

  /* ── Multi-market results ────────────────────────────────────────────────── */
  const [marketResults,  setMarketResults]  = useState({});  // { 'Sverige': { description, metaTitle, metaDescription } }
  const [marketProgress, setMarketProgress] = useState({});  // { 'Sverige': 'idle'|'loading'|'done'|'error' }
  const [activeMarket,   setActiveMarket]   = useState(null);
  const [isGenerating,   setIsGenerating]   = useState(false);

  /* ── A/B mode (single market only) ──────────────────────────────────────── */
  const [abMode, setAbMode]           = useState(false);
  const [outputA, setOutputA]         = useState('');
  const [outputB, setOutputB]         = useState('');
  const [isLoadingA, setIsLoadingA]   = useState(false);
  const [isLoadingB, setIsLoadingB]   = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);

  /* ── Display ─────────────────────────────────────────────────────────────── */
  const [showHtmlTags, setShowHtmlTags] = useState(true);
  const [error,        setError]        = useState('');
  const [copied,       setCopied]       = useState(null);
  const [savedFavorite, setSavedFavorite] = useState(false);

  /* ── Norce publish state ────────────────────────────────────────────────── */
  const [partNo,         setPartNo]         = useState('');
  const [norceStatus,    setNorceStatus]    = useState({}); // { displayMarket: { status, message } }
  const [showNorceModal, setShowNorceModal] = useState(false);
  const [isPublishing,   setIsPublishing]   = useState(false);

  /* ── Data context ────────────────────────────────────────────────────────── */
  const {
    templates, categories, vocabulary, vocabCollections,
    addToHistory, addFavorite, addLog: dataAddLog, model, addUsageLog,
  } = useData();
  const { brandGuidelines, categoryTemplates } = templates;
  const isCategoryEnabled = category && categoryTemplates[category]?.trim().length > 0;
  const [activeCollections, setActiveCollections] = useState(null);
  const vocabForPrompt = activeCollections
    ? vocabulary.filter(e => activeCollections.includes(e.collection || 'Standard'))
    : vocabulary;
  const promptCtx = { brandGuidelines, categoryTemplates, vocabulary: vocabForPrompt, model };

  /* ── Computed ────────────────────────────────────────────────────────────── */
  const anyFieldEnabled = genSwitches.desc || genSwitches.metaTitle || genSwitches.metaDesc;
  const showAbMode      = selectedMarkets.length <= 1;
  const canGenerate     = productName.trim() && category && features.trim()
                          && selectedMarkets.length > 0 && isCategoryEnabled && anyFieldEnabled;
  const isAnyLoading    = isGenerating || isLoadingA || isLoadingB;
  const hasAnyResult    = Object.keys(marketResults).length > 0;
  const allDone         = selectedMarkets.length > 0
                          && selectedMarkets.every(m => marketProgress[m] === 'done');
  const activeResult    = activeMarket ? (marketResults[activeMarket] || {}) : {};
  const wordCountA      = countWords(outputA);
  const wordCountB      = countWords(outputB);
  const canPublishToNorce = allDone && partNo.trim().length > 0 && !isPublishing;

  /* ── Prefill (Generera liknande) ─────────────────────────────────────────── */
  useEffect(() => {
    if (!prefill) return;
    setProductName(prefill.productName || '');
    setCategory(prefill.category || '');
    const mktDisplay = MARKET_CODE_TO_DISPLAY[prefill.market] || prefill.market || '';
    setSelectedMarkets(mktDisplay && MARKETS.includes(mktDisplay) ? [mktDisplay] : []);
    setFeatures(prefill.features || '');
    setMarketResults({}); setMarketProgress({}); setActiveMarket(null);
    setSavedFavorite(false);
    if (onPrefillUsed) onPrefillUsed();
    toast.info(`Formuläret ifyllt med ${prefill.productName} — justera och generera!`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  /* ── Market selection helpers ────────────────────────────────────────────── */
  const toggleMarket = mkt =>
    setSelectedMarkets(prev => prev.includes(mkt) ? prev.filter(m => m !== mkt) : [...prev, mkt]);
  const toggleAllMarkets = () =>
    setSelectedMarkets(prev => prev.length === MARKETS.length ? [] : [...MARKETS]);

  /* ── Sequential multi-market generation ─────────────────────────────────── */
  const handleGenerate = async () => {
    if (abMode && showAbMode) { handleGenerateAB(); return; }
    if (!anyFieldEnabled) { toast.warning('Aktivera minst ett fält att generera'); return; }

    setIsGenerating(true);
    setError('');
    setSavedFavorite(false);
    setMarketResults({});
    setNorceStatus({});

    const initProgress = {};
    selectedMarkets.forEach(m => { initProgress[m] = 'idle'; });
    setMarketProgress(initProgress);
    setActiveMarket(selectedMarkets[0]);

    const startTime = Date.now();
    let successCount = 0;
    let failCount = 0;
    const allCodes = selectedMarkets.map(m => MARKET_DISPLAY_TO_CODE[m] || m);
    const localResults = {}; // track results locally (no stale closure from state)

    for (const mkt of selectedMarkets) {
      setMarketProgress(prev => ({ ...prev, [mkt]: 'loading' }));
      setActiveMarket(mkt);
      try {
        const result = await generateAllFields(productName, category, features, mkt, promptCtx);
        const mktResult = {
          description:     genSwitches.desc      ? (result.description     || '') : '',
          metaTitle:       genSwitches.metaTitle  ? (result.metaTitle       || '') : '',
          metaDescription: genSwitches.metaDesc   ? (result.metaDescription || '') : '',
        };
        localResults[mkt] = mktResult;
        setMarketResults(prev => ({ ...prev, [mkt]: mktResult }));
        setMarketProgress(prev => ({ ...prev, [mkt]: 'done' }));
        successCount++;
        const mktCode = MARKET_DISPLAY_TO_CODE[mkt] || mkt;
        addToHistory({ productName, category, market: mkt, wordCount: countWordsHtml(result.description), source: 'single' });
        if (result.usage) addUsageLog({ ...result.usage, market: mktCode, category, source: 'single', module: 'generate' });
      } catch (err) {
        setMarketProgress(prev => ({ ...prev, [mkt]: 'error' }));
        failCount++;
        console.error(`Generation failed for ${mkt}:`, err);
      }
    }

    // Save first market's content in outputData for log detail panel
    const firstMkt    = selectedMarkets[0];
    const firstResult = localResults[firstMkt] || {};
    const firstCode   = MARKET_DISPLAY_TO_CODE[firstMkt] || firstMkt;

    dataAddLog({
      type: 'single', productName, markets: allCodes,
      products: selectedMarkets.length, success: successCount, failed: failCount,
      durationSeconds: Math.round((Date.now() - startTime) / 1000),
      outputData: {
        category,
        model:           promptCtx.model,
        markets:         allCodes,
        market:          firstCode,
        description:     firstResult.description     || '',
        metaTitle:       firstResult.metaTitle        || '',
        metaDescription: firstResult.metaDescription  || '',
        wordCount:       countWordsHtml(firstResult.description || ''),
      },
    });

    setIsGenerating(false);
    if (failCount === 0) {
      toast.success(`${successCount} marknad${successCount !== 1 ? 'er' : ''} genererade!`);
    } else {
      toast.warning(`${successCount} OK, ${failCount} misslyckades`);
      setError(`${failCount} marknad${failCount !== 1 ? 'er' : ''} misslyckades. Försök igen.`);
    }
  };

  /* ── A/B Mode (single market) ────────────────────────────────────────────── */
  const handleGenerateAB = async () => {
    setIsLoadingA(true); setIsLoadingB(true);
    setError(''); setSelectedVariant(null); setSavedFavorite(false);
    setOutputA(''); setOutputB('');
    const startTime = Date.now();
    const mkt = selectedMarkets[0] || 'Sverige';
    const generateVariant = async (setOut, setLoading, variant) => {
      try {
        const { text } = await generateOne(productName, category, features, mkt, promptCtx);
        setOut(text);
        addToHistory({ productName, category, market: mkt, wordCount: countWords(text), source: 'single', variant });
      } catch (err) { setOut(`Fel: ${err.message}`); }
      finally { setLoading(false); }
    };
    await Promise.all([
      generateVariant(setOutputA, setIsLoadingA, 'A'),
      generateVariant(setOutputB, setIsLoadingB, 'B'),
    ]);
    const mktCode = MARKET_DISPLAY_TO_CODE[mkt] || mkt;
    dataAddLog({ type: 'ab', productName, markets: [mktCode], products: 2, success: 2, failed: 0,
      durationSeconds: Math.round((Date.now() - startTime) / 1000) });
    toast.success('Två varianter genererade — välj din favorit!');
  };

  const handleSelectVariant = variant => {
    setSelectedVariant(variant);
    // Copy selected variant into marketResults for consistency
    const mkt = selectedMarkets[0] || 'Sverige';
    setMarketResults({ [mkt]: { description: variant === 'A' ? outputA : outputB, metaTitle: '', metaDescription: '' } });
    setActiveMarket(mkt);
    toast.success(`Variant ${variant} vald`);
  };

  /* ── Update active result ─────────────────────────────────────────────────── */
  const updateActiveResult = (field, value) => {
    if (!activeMarket) return;
    setMarketResults(prev => ({ ...prev, [activeMarket]: { ...prev[activeMarket], [field]: value } }));
  };

  /* ── Copy helper ─────────────────────────────────────────────────────────── */
  const handleCopy = async (text, field = 'default') => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(field);
      toast.success('Kopierad!');
      setTimeout(() => setCopied(null), 2000);
    } catch { toast.error('Kunde inte kopiera'); }
  };

  /* ── Save all markets as separate favorites ─────────────────────────────── */
  const handleSaveAllFavorites = () => {
    let count = 0;
    selectedMarkets.forEach((mkt, i) => {
      const result = marketResults[mkt];
      if (!result?.description) return;
      addFavorite({
        id: Date.now() + i,
        productName, category, market: MARKET_DISPLAY_TO_CODE[mkt] || mkt,
        description: result.description, output: result.description,
        wordCount: countWordsHtml(result.description),
        features,
        metaTitle:       result.metaTitle       || '',
        metaDescription: result.metaDescription || '',
      });
      count++;
    });
    setSavedFavorite(true);
    toast.success(`${count} favorit${count !== 1 ? 'er' : ''} sparade!`);
  };

  /* ── Export all markets to .xlsx ─────────────────────────────────────────── */
  const handleExportAll = () => {
    const wb = XLSX.utils.book_new();
    for (const mkt of selectedMarkets) {
      const result = marketResults[mkt];
      if (!result) continue;
      const rows = [
        ['Description',               stripHtml(result.description || ''), ''],
        ['Meta title override',       result.metaTitle              || '', ''],
        ['Meta description override', result.metaDescription        || '', ''],
      ];
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{ wch: 26 }, { wch: 90 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws, mkt.slice(0, 31));
    }
    XLSX.writeFile(wb, `${productName || 'genererat'}-alla-marknader.xlsx`);
    toast.success('Excel-fil nedladdad!');
  };

  /* ── Publish to Norce ────────────────────────────────────────────────────── */
  const handlePublishToNorce = async (marketsToPublish) => {
    setIsPublishing(true);
    setShowNorceModal(false);

    // Set all targeted markets to loading
    const initStatus = {};
    marketsToPublish.forEach(m => { initStatus[m] = { status: 'loading', message: '' }; });
    setNorceStatus(prev => ({ ...prev, ...initStatus }));

    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const products = marketsToPublish.map(mkt => ({
      partNo: partNo.trim(),
      market: MARKET_DISPLAY_TO_CODE[mkt] || mkt,
      description:      marketResults[mkt]?.description      || '',
      metaTitle:        marketResults[mkt]?.metaTitle        || '',
      metaDescription:  marketResults[mkt]?.metaDescription  || '',
    }));

    try {
      const response = await fetch(`${apiUrl}/api/norce/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ products }),
      });

      if (!response.ok) {
        let errMsg = 'Okänt fel';
        try { const err = await response.json(); errMsg = err.detail || errMsg; } catch (parseErr) { console.error('[Norce] Failed to parse error response:', parseErr); }
        const errStatus = {};
        marketsToPublish.forEach(m => { errStatus[m] = { status: 'error', message: errMsg }; });
        setNorceStatus(prev => ({ ...prev, ...errStatus }));
        toast.error(`Publicering misslyckades: ${errMsg}`);
        return;
      }

      const data = await response.json();
      const newStatus = {};
      (data.results || []).forEach(result => {
        // Map market code back to display name
        const mktDisplay = Object.entries(MARKET_DISPLAY_TO_CODE)
          .find(([, v]) => v === result.market)?.[0] || result.market;
        newStatus[mktDisplay] = {
          status:  result.success ? 'done' : 'error',
          message: result.error  || '',
        };
      });
      setNorceStatus(prev => ({ ...prev, ...newStatus }));

      const successCount = (data.results || []).filter(r => r.success).length;
      const failCount    = (data.results || []).filter(r => !r.success).length;
      if (failCount === 0) {
        toast.success(`${successCount} marknad${successCount !== 1 ? 'er' : ''} publicerade till Norce!`);
      } else {
        toast.warning(`${successCount} publicerade, ${failCount} misslyckades`);
      }
    } catch (err) {
      const errStatus = {};
      marketsToPublish.forEach(m => { errStatus[m] = { status: 'error', message: err.message }; });
      setNorceStatus(prev => ({ ...prev, ...errStatus }));
      toast.error('Nätverksfel vid publicering till Norce');
    } finally {
      setIsPublishing(false);
    }
  };

  const toggleSwitch = key => setGenSwitches(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleAbMode = () => {
    setAbMode(v => !v);
    setOutputA(''); setOutputB(''); setSelectedVariant(null);
    if (!abMode) toast.info('A/B-läge aktiverat — genererar två varianter att jämföra');
  };

  /* ── Render ──────────────────────────────────────────────────────────────── */
  const doneMkts = selectedMarkets.filter(m => marketProgress[m] === 'done');

  return (
    <div className={`generate-page${abMode && showAbMode ? ' generate-page--ab' : ''}`}>

      {/* ── Norce Confirmation Modal ────────────────────────────────────────── */}
      {showNorceModal && (
        <div className="norce-pub-overlay" data-testid="norce-modal-overlay" onClick={() => setShowNorceModal(false)}>
          <div className="norce-pub-modal" onClick={e => e.stopPropagation()}>
            <div className="norce-pub-modal-header">
              <div className="norce-pub-modal-title-row">
                <Upload size={18} className="norce-pub-modal-icon" />
                <h3 className="norce-pub-modal-title">Publicera till Norce</h3>
              </div>
              <button className="norce-pub-modal-close" onClick={() => setShowNorceModal(false)} data-testid="norce-modal-close">
                <X size={16} />
              </button>
            </div>
            <div className="norce-pub-modal-body">
              <div className="norce-pub-info-row">
                <span className="norce-pub-info-label">Artikelnummer</span>
                <span className="norce-pub-info-value norce-pub-partno" data-testid="norce-modal-partno">{partNo}</span>
              </div>
              <div className="norce-pub-info-row">
                <span className="norce-pub-info-label">Marknader att publicera</span>
              </div>
              <ul className="norce-pub-market-list">
                {doneMkts.map(mkt => (
                  <li key={mkt} className="norce-pub-market-item" data-testid={`norce-modal-market-${mkt}`}>
                    <Check size={12} className="norce-pub-mkt-check" />
                    <span className="norce-pub-mkt-name"><MarketFlag market={mkt} /> {mkt}</span>
                    <span className="norce-pub-content-tags">
                      {marketResults[mkt]?.description      && <span className="norce-pub-tag">Beskrivning</span>}
                      {marketResults[mkt]?.metaTitle        && <span className="norce-pub-tag">Meta Title</span>}
                      {marketResults[mkt]?.metaDescription  && <span className="norce-pub-tag">Meta Desc</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="norce-pub-modal-footer">
              <button className="norce-pub-cancel" onClick={() => setShowNorceModal(false)} data-testid="norce-modal-cancel">
                Avbryt
              </button>
              <button
                className="norce-pub-confirm"
                data-testid="norce-modal-confirm"
                onClick={() => handlePublishToNorce(doneMkts)}
              >
                <Upload size={14} />
                Publicera {doneMkts.length} marknad{doneMkts.length !== 1 ? 'er' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Left panel — form ─────────────────────────────────────────────── */}
      <aside className="left-panel">
        <div className="panel-content">
          <div className="panel-intro">
            <h1 className="panel-title">Produktinformation</h1>
            <p className="panel-desc">Fyll i uppgifterna för att generera innehåll.</p>
          </div>

          <div className="form-stack">
            {/* Product name */}
            <div className="field">
              <label className="field-label"><Tag size={12} className="label-icon" />Produktnamn</label>
              <input data-testid="product-name-input" type="text" className="field-input"
                placeholder="T.ex. Stödstrumpa Bambu Pro" value={productName}
                onChange={e => setProductName(e.target.value)} />
            </div>

            {/* Category */}
            <div className="field">
              <label className="field-label"><Layers size={12} className="label-icon" />Kategori</label>
              <div className="select-wrap">
                <select data-testid="category-select" className="field-input field-select"
                  value={category} onChange={e => setCategory(e.target.value)}>
                  <option value="">Välj kategori...</option>
                  {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
                <svg className="select-chevron" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" />
                </svg>
              </div>
              {category && !isCategoryEnabled && (
                <div data-testid="template-coming-soon" className="coming-soon">
                  <AlertTriangle size={13} />
                  <span>Mall för {category} saknas — lägg till den under Mallar för att aktivera</span>
                </div>
              )}
            </div>

            {/* Features */}
            <div className="field">
              <label className="field-label field-label-row">
                <span>Viktiga egenskaper</span>
                <span className="field-hint">En egenskap per rad</span>
              </label>
              <textarea data-testid="features-textarea" className="field-input field-textarea"
                placeholder={`Kompressionsklass I\nMjuk resår\nFukttransporterande material`}
                value={features} onChange={e => setFeatures(e.target.value)} rows={5} />
            </div>

            {/* PartNo for Norce */}
            <div className="field">
              <label className="field-label field-label-row">
                <span>Artikelnummer (PartNo)</span>
                <span className="field-hint">Krävs för publicering till Norce</span>
              </label>
              <input
                data-testid="part-no-input"
                type="text"
                className="field-input"
                placeholder="T.ex. BIRK-1234-SE"
                value={partNo}
                onChange={e => setPartNo(e.target.value)}
              />
            </div>

            {/* Market checkbox group */}
            <div className="field">
              <label className="field-label field-label-row">
                <span className="field-label-left"><Globe size={12} className="label-icon" />Marknader</span>
                <button type="button" className="select-all-markets-btn" onClick={toggleAllMarkets}
                  data-testid="toggle-all-markets">
                  {selectedMarkets.length === MARKETS.length ? 'Avmarkera alla' : 'Välj alla'}
                </button>
              </label>
              <div className="market-checkboxes" data-testid="market-checkboxes">
                {MARKETS.map(mkt => (
                  <label key={mkt}
                    className={`market-checkbox-label${selectedMarkets.includes(mkt) ? ' market-checkbox-label--on' : ''}`}
                    data-testid={`market-checkbox-${mkt}`}>
                    <input type="checkbox" checked={selectedMarkets.includes(mkt)}
                      onChange={() => toggleMarket(mkt)} />
                    <span><MarketFlag market={mkt} /> {mkt}</span>
                  </label>
                ))}
              </div>
              {selectedMarkets.length === 0 && (
                <p className="field-hint" style={{ color: '#ef4444', marginTop: 4 }}>
                  Välj minst en marknad
                </p>
              )}
            </div>

            {/* Generation switches */}
            <div className="field">
              <label className="field-label">Fält att generera</label>
              <div className="gen-switches" data-testid="gen-switches">
                <GenSwitch checked={genSwitches.desc}      onChange={() => toggleSwitch('desc')}      label="Produktbeskrivning" testId="switch-desc" />
                <GenSwitch checked={genSwitches.metaTitle} onChange={() => toggleSwitch('metaTitle')} label="Meta Title"         testId="switch-meta-title" />
                <GenSwitch checked={genSwitches.metaDesc}  onChange={() => toggleSwitch('metaDesc')}  label="Meta Description"   testId="switch-meta-desc" />
              </div>
            </div>

            {/* Vocabulary collection selector */}
            <VocabCollectionSelector vocabCollections={vocabCollections}
              activeCollections={activeCollections} onChange={setActiveCollections} />

            {/* A/B Mode — only for single market + description only */}
            {showAbMode && !genSwitches.metaTitle && !genSwitches.metaDesc && (
              <div className="ab-toggle-wrap">
                <button data-testid="ab-mode-toggle"
                  className={`ab-toggle${abMode ? ' ab-toggle--active' : ''}`} onClick={toggleAbMode}>
                  <Columns size={14} />
                  <span>A/B-läge</span>
                  <span className={`ab-toggle-indicator${abMode ? ' ab-toggle-indicator--on' : ''}`} />
                </button>
                <span className="ab-toggle-hint">
                  {abMode ? 'Genererar två varianter' : 'Genererar en variant'}
                </span>
              </div>
            )}

            {/* Generate button */}
            <button data-testid="generate-button"
              className={`generate-btn${canGenerate && !isAnyLoading ? ' generate-btn--active' : ''}`}
              onClick={handleGenerate} disabled={!canGenerate || isAnyLoading}>
              {isAnyLoading ? (
                <><span className="spinner" />Genererar{abMode && showAbMode ? ' varianter' : selectedMarkets.length > 1 ? ` (${selectedMarkets.length} marknader)` : ''}...</>
              ) : (
                <><Sparkles size={15} />{abMode && showAbMode ? 'Skapa två varianter' : 'Skapa innehåll'}</>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* ── Right panel — results ──────────────────────────────────────────── */}
      {(!abMode || !showAbMode) ? (
        <section className="right-panel">
          <div className="panel-content">
            <div className="output-header">
              <div>
                <h2 className="panel-title">Genererat innehåll</h2>
                <p className="panel-desc">Redigera texten direkt i fälten nedan.</p>
              </div>
            </div>

            {error && (
              <div data-testid="error-message" className="alert alert--error">
                <AlertTriangle size={14} /><span>{error}</span>
              </div>
            )}

            {/* Per-market progress strip */}
            {Object.keys(marketProgress).length > 0 && (
              <div className="market-progress-strip" data-testid="market-progress">
                {selectedMarkets.map(mkt => (
                  <div key={mkt}
                    className={`mkt-progress-chip mkt-progress-chip--${marketProgress[mkt] || 'idle'}`}>
                    <MarketFlag market={mkt} />
                    <span className="mkt-progress-name">{mkt}</span>
                    {marketProgress[mkt] === 'loading' && <span className="spinner-sm" />}
                    {marketProgress[mkt] === 'done'    && <Check size={11} />}
                    {marketProgress[mkt] === 'error'   && <AlertTriangle size={11} />}
                  </div>
                ))}
              </div>
            )}

            {/* Market result tabs */}
            {hasAnyResult && selectedMarkets.length > 1 && (
              <div className="market-tabs" data-testid="market-tabs">
                {selectedMarkets.map(mkt => (
                  <button key={mkt}
                    className={`market-tab${activeMarket === mkt ? ' market-tab--active' : ''} market-tab--${marketProgress[mkt] || 'idle'}`}
                    onClick={() => setActiveMarket(mkt)}
                    data-testid={`market-tab-${mkt}`}>
                    <MarketFlag market={mkt} />
                    <span>{mkt}</span>
                    {marketProgress[mkt] === 'done' && <span className="market-tab-done">✓</span>}
                    {marketProgress[mkt] === 'error' && <span className="market-tab-error">✗</span>}
                  </button>
                ))}
              </div>
            )}

            {/* Loading shimmer */}
            {isGenerating && !activeResult.description && (
              <div data-testid="loading-state" className="loading-state">
                <div className="shimmer-lines">
                  {[100, 85, 92, 70, 88, 60, 78, 95, 65, 80].map((w, i) => (
                    <div key={i} className="shimmer-line" style={{ width: `${w}%`, animationDelay: `${i * 80}ms` }} />
                  ))}
                </div>
                <p className="loading-label">
                  Genererar {activeMarket ? <><MarketFlag market={activeMarket} size="1em" /> {activeMarket}</> : ''} ...
                </p>
              </div>
            )}

            {/* Output fields for active market */}
            {hasAnyResult && activeMarket && (
              <div className="output-fields" data-testid="output-fields">

                {/* Kopiera alla */}
                {(activeResult.description || activeResult.metaTitle || activeResult.metaDescription) && (
                  <div className="kopiera-alla-row">
                    <button data-testid="copy-all-btn"
                      className={`kopiera-alla-btn${copied === 'all' ? ' kopiera-alla-btn--done' : ''}`}
                      onClick={async () => {
                        const desc = showHtmlTags ? activeResult.description : stripHtml(activeResult.description || '');
                        const parts = [];
                        if (desc)                          parts.push(`PRODUKTBESKRIVNING:\n${desc}`);
                        if (activeResult.metaTitle)        parts.push(`META TITLE:\n${activeResult.metaTitle}`);
                        if (activeResult.metaDescription)  parts.push(`META DESCRIPTION:\n${activeResult.metaDescription}`);
                        if (!parts.length) return;
                        await navigator.clipboard.writeText(parts.join('\n\n'));
                        setCopied('all');
                        toast.success('Alla fält kopierade!');
                        setTimeout(() => setCopied(null), 2000);
                      }}>
                      {copied === 'all' ? <Check size={13} /> : <Copy size={13} />}
                      {copied === 'all' ? 'Kopierade!' : 'Kopiera alla'}
                    </button>
                  </div>
                )}

                {/* ── Description ── */}
                {genSwitches.desc && (
                  <div className="output-field-block" data-testid="output-block-desc">
                    <div className="output-field-header">
                      <span className="output-field-title">Produktbeskrivning</span>
                      <div className="output-field-actions">
                        {activeResult.description && (
                          <span data-testid="word-count-badge"
                            className={`word-badge${isWordCountValid(countWordsHtml(activeResult.description)) ? ' word-badge--ok' : ' word-badge--warn'}`}>
                            {isWordCountValid(countWordsHtml(activeResult.description)) ? <Check size={11} /> : <AlertTriangle size={11} />}
                            <span>{countWordsHtml(activeResult.description)} / 150–220 ord</span>
                          </span>
                        )}
                        {/* HTML toggle */}
                        <label className="html-toggle-chip" data-testid="html-toggle-label">
                          <span className="html-toggle-text">HTML</span>
                          <button type="button"
                            className={`toggle-switch toggle-switch--xs${showHtmlTags ? ' toggle-switch--on' : ''}`}
                            onClick={() => setShowHtmlTags(v => !v)}
                            data-testid="html-toggle">
                            <span className="toggle-knob" />
                          </button>
                        </label>
                        <button data-testid="copy-button"
                          className={`field-copy-btn${activeResult.description ? ' field-copy-btn--active' : ''}`}
                          onClick={() => handleCopy(showHtmlTags ? activeResult.description : stripHtml(activeResult.description || ''), 'desc')}
                          disabled={!activeResult.description}>
                          {copied === 'desc' ? <Check size={12} /> : <Copy size={12} />}
                          {copied === 'desc' ? 'Kopierad' : 'Kopiera'}
                        </button>
                      </div>
                    </div>
                    <textarea data-testid="output-textarea" className="output-textarea"
                      value={showHtmlTags
                        ? (activeResult.description || '')
                        : stripHtml(activeResult.description || '')}
                      onChange={e => showHtmlTags && updateActiveResult('description', e.target.value)}
                      readOnly={!showHtmlTags}
                      placeholder="Produktbeskrivning visas här..." />
                    {!showHtmlTags && (
                      <p className="html-view-note">Visningsläge — aktivera HTML för att redigera</p>
                    )}
                  </div>
                )}

                {/* ── Meta Title ── */}
                {genSwitches.metaTitle && (
                  <div className="output-field-block" data-testid="output-block-meta-title">
                    <div className="output-field-header">
                      <span className="output-field-title">Meta Title</span>
                      <div className="output-field-actions">
                        {activeResult.metaTitle && (
                          <span className={`char-badge${activeResult.metaTitle.length >= 50 && activeResult.metaTitle.length <= 55 ? ' char-badge--ok' : ' char-badge--warn'}`}>
                            {activeResult.metaTitle.length} / 50–55 tkn
                          </span>
                        )}
                        <button data-testid="copy-meta-title"
                          className={`field-copy-btn${activeResult.metaTitle ? ' field-copy-btn--active' : ''}`}
                          onClick={() => handleCopy(activeResult.metaTitle, 'metaTitle')}
                          disabled={!activeResult.metaTitle}>
                          {copied === 'metaTitle' ? <Check size={12} /> : <Copy size={12} />}
                          {copied === 'metaTitle' ? 'Kopierad' : 'Kopiera'}
                        </button>
                      </div>
                    </div>
                    <input data-testid="output-meta-title" type="text" className="output-text-input"
                      value={activeResult.metaTitle || ''}
                      onChange={e => updateActiveResult('metaTitle', e.target.value)}
                      placeholder="Meta title visas här..." />
                  </div>
                )}

                {/* ── Meta Description ── */}
                {genSwitches.metaDesc && (
                  <div className="output-field-block" data-testid="output-block-meta-desc">
                    <div className="output-field-header">
                      <span className="output-field-title">Meta Description</span>
                      <div className="output-field-actions">
                        {activeResult.metaDescription && (
                          <span className={`char-badge${activeResult.metaDescription.length >= 150 && activeResult.metaDescription.length <= 160 ? ' char-badge--ok' : ' char-badge--warn'}`}>
                            {activeResult.metaDescription.length} / 150–160 tkn
                          </span>
                        )}
                        <button data-testid="copy-meta-desc"
                          className={`field-copy-btn${activeResult.metaDescription ? ' field-copy-btn--active' : ''}`}
                          onClick={() => handleCopy(activeResult.metaDescription, 'metaDesc')}
                          disabled={!activeResult.metaDescription}>
                          {copied === 'metaDesc' ? <Check size={12} /> : <Copy size={12} />}
                          {copied === 'metaDesc' ? 'Kopierad' : 'Kopiera'}
                        </button>
                      </div>
                    </div>
                    <textarea data-testid="output-meta-desc" className="output-textarea output-textarea--short"
                      rows={3} value={activeResult.metaDescription || ''}
                      onChange={e => updateActiveResult('metaDescription', e.target.value)}
                      placeholder="Meta description visas här..." />
                  </div>
                )}
              </div>
            )}

            {/* Action row */}
            {!isGenerating && (
              <div className="action-row">
                {hasAnyResult && (
                  <>
                    {/* Export all markets */}
                    <button data-testid="export-all-btn" className="action-btn action-btn--export"
                      onClick={handleExportAll}>
                      <Download size={14} />
                      <span>Exportera alla marknader</span>
                    </button>

                    {/* Skicka till Norce */}
                    <button
                      data-testid="publish-norce-btn"
                      className={`action-btn action-btn--norce${canPublishToNorce ? ' action-btn--norce-active' : ''}`}
                      onClick={() => setShowNorceModal(true)}
                      disabled={!canPublishToNorce}
                      title={!partNo.trim() ? 'Ange artikelnummer (PartNo) för att publicera' : !allDone ? 'Vänta tills alla marknader är klara' : ''}
                    >
                      {isPublishing ? (
                        <><span className="spinner" />Publicerar...</>
                      ) : (
                        <><Upload size={14} /><span>Skicka till Norce</span></>
                      )}
                    </button>

                    {/* Regenerate */}
                    <button data-testid="regenerate-button"
                      className={`action-btn${canGenerate ? ' action-btn--active' : ''}`}
                      onClick={handleGenerate} disabled={!canGenerate || isAnyLoading}>
                      <RefreshCw size={14} /><span>Skapa ny</span>
                    </button>

                    {/* Save favorites */}
                    <button data-testid="save-favorite-button"
                      className={`action-btn action-btn--favorite${allDone && !savedFavorite ? ' action-btn--active' : ''}${savedFavorite ? ' action-btn--saved' : ''}`}
                      onClick={handleSaveAllFavorites}
                      disabled={!allDone || savedFavorite}>
                      <Heart size={14} className={savedFavorite ? 'heart-filled' : ''} />
                      <span>{savedFavorite ? 'Sparade!' : `Spara ${selectedMarkets.length > 1 ? 'alla favoriter' : 'favorit'}`}</span>
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Norce publish status strip */}
            {Object.keys(norceStatus).length > 0 && (
              <div className="norce-pub-status-strip" data-testid="norce-status-strip">
                <span className="norce-pub-status-label">Norce</span>
                {selectedMarkets.map(mkt => {
                  const s = norceStatus[mkt];
                  if (!s) return null;
                  return (
                    <div key={mkt} className={`norce-pub-chip norce-pub-chip--${s.status}`}
                      data-testid={`norce-chip-${mkt}`} title={s.message || ''}>
                      <MarketFlag market={mkt} />
                      <span className="norce-pub-chip-name">{mkt}</span>
                      {s.status === 'loading' && <span className="spinner-sm" />}
                      {s.status === 'done'    && <Check size={11} />}
                      {s.status === 'error'   && (
                        <>
                          <AlertTriangle size={11} />
                          <button
                            className="norce-pub-retry-btn"
                            data-testid={`norce-retry-${mkt}`}
                            onClick={() => handlePublishToNorce([mkt])}
                            disabled={isPublishing}
                          >
                            Försök igen
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Empty state */}
            {!hasAnyResult && !isGenerating && (
              <div className="empty-output-state" data-testid="empty-output">
                {canGenerate ? (
                  <>
                    <Sparkles size={32} className="empty-sparkle" />
                    <p>Klicka på "Skapa innehåll" för att generera</p>
                  </>
                ) : (
                  <p className="output-hint">
                    Fyll i produktnamn, kategori, egenskaper och välj minst en marknad.
                  </p>
                )}
              </div>
            )}
          </div>
        </section>
      ) : (
        /* A/B Mode — single market only */
        <section className="right-panel right-panel--ab">
          <div className="ab-header">
            <h2 className="panel-title">Jämför varianter</h2>
            <p className="panel-desc">Klicka "Välj" på den variant du föredrar.</p>
          </div>
          {error && (
            <div className="ab-error">
              <div className="alert alert--error"><AlertTriangle size={14} /><span>{error}</span></div>
            </div>
          )}
          <div className="ab-grid">
            {[
              { key: 'A', output: outputA, setOut: setOutputA, isLoading: isLoadingA, wc: wordCountA },
              { key: 'B', output: outputB, setOut: setOutputB, isLoading: isLoadingB, wc: wordCountB },
            ].map(v => (
              <div key={v.key} className={`ab-card${selectedVariant === v.key ? ' ab-card--selected' : ''}`}>
                <div className="ab-card-header">
                  <span className="ab-variant-label">Variant {v.key}</span>
                  {v.output && (
                    <span className={`ab-wc-badge${isWordCountValid(v.wc) ? ' ab-wc-badge--ok' : ' ab-wc-badge--warn'}`}>
                      {v.wc} ord
                    </span>
                  )}
                </div>
                {v.isLoading ? (
                  <div className="ab-loading">
                    <div className="shimmer-lines">
                      {[100, 85, 92, 70, 88].map((w, i) => (
                        <div key={i} className="shimmer-line" style={{ width: `${w}%`, animationDelay: `${i * 80}ms` }} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <textarea className="ab-textarea" value={v.output}
                    onChange={e => v.setOut(e.target.value)}
                    placeholder={`Variant ${v.key} genereras...`}
                    readOnly={selectedVariant && selectedVariant !== v.key} />
                )}
                <div className="ab-card-actions">
                  <button className="ab-copy-btn" onClick={() => handleCopy(v.output, `ab-${v.key}`)}
                    disabled={!v.output || v.isLoading}>
                    <Copy size={12} />
                  </button>
                  <button data-testid={`select-variant-${v.key.toLowerCase()}`}
                    className={`ab-select-btn${selectedVariant === v.key ? ' ab-select-btn--selected' : ''}`}
                    onClick={() => handleSelectVariant(v.key)} disabled={!v.output || v.isLoading}>
                    {selectedVariant === v.key ? <><Check size={14} />Vald</> : <><ArrowRight size={14} />Välj {v.key}</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
          {selectedVariant && (
            <div className="ab-selected-actions">
              <span className="ab-selected-label"><Check size={14} />Variant {selectedVariant} vald</span>
              <button data-testid="save-selected-favorite"
                className={`action-btn action-btn--favorite${!savedFavorite ? ' action-btn--active' : ' action-btn--saved'}`}
                onClick={handleSaveAllFavorites} disabled={savedFavorite}>
                <Heart size={14} className={savedFavorite ? 'heart-filled' : ''} />
                <span>{savedFavorite ? 'Sparad!' : 'Spara till favoriter'}</span>
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
